import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Package, CheckCircle2, Send } from "lucide-react";

const STATUS = { pending: "wacht op keuring", accepted: "goedgekeurd door ontvanger", rejected: "afgekeurd door ontvanger" };

function ApproveDialog({ wp, open, onCancel, onConfirm }) {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    setOk(false);
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="rounded-none max-w-md" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="approve-work-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-grotesk">Werkproduct afronden</DialogTitle>
          <DialogDescription>"{wp?.name}" op 100% zetten.</DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-2 border border-zinc-900 p-3 text-sm cursor-pointer">
          <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} className="rounded-none mt-0.5" data-testid="approve-work-product-checkbox" />
          <span>Het werkproduct is getest en goed bevonden.</span>
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="rounded-none" onClick={onCancel} data-testid="approve-cancel-button">Annuleren</Button>
          <Button className="rounded-none bg-zinc-900" disabled={!ok} onClick={onConfirm} data-testid="approve-confirm-button">Afronden</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ wp, open, onClose, targets, onSend }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle className="font-grotesk">Werkproduct doorgeven</DialogTitle>
          <DialogDescription>Geef "{wp?.name}" door aan een systeem waarmee je een verbindingslijn hebt.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          {targets.map((t) => (
            <button key={`${t.node_id}-${t.system}`} onClick={() => onSend(t)} className="w-full text-left border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-50 px-3 py-2 text-sm flex items-center justify-between transition-colors" data-testid={`transfer-target-${t.node_id === targets[0].node_id ? "same" : "lower"}-${t.system}`}>
              {t.label} <Send className="h-3.5 w-3.5 text-zinc-400" />
            </button>
          ))}
        </div>
        <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="transfer-skip-button">Later doorgeven</Button>
      </DialogContent>
    </Dialog>
  );
}

function DescriptionField({ value, canEdit, onSave, wpId }) {
  const [v, setV] = useState(value);
  useEffect(() => {
    setV(value);
  }, [value]);
  if (!canEdit) return value ? <p className="mt-1 pl-4 text-[11px] text-zinc-600 whitespace-pre-line" data-testid={`work-product-description-${wpId}`}>{value}</p> : null;
  return (
    <Textarea
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v.trim() !== (value || "") && onSave(v)}
      placeholder="Beschrijf dit werkproduct specifiek…"
      rows={2}
      className="mt-1 ml-4 w-[calc(100%-1rem)] rounded-none border-zinc-300 text-[11px] min-h-0 py-1 px-2"
      data-testid={`work-product-description-input-${wpId}`}
    />
  );
}

function ProgressRow({ wp, value, approved, canEdit, onSave, onTransfer, sent, description, onDescribe }) {
  const [v, setV] = useState(value);
  const [ask, setAsk] = useState(false);
  useEffect(() => {
    setV(value);
  }, [value]);
  const commit = () => {
    const n = Math.max(0, Math.min(100, Math.round(Number(v) || 0)));
    if (n === value) return setV(n);
    if (n === 100) return setAsk(true);
    setV(n);
    onSave(wp.id, n, false);
  };
  return (
    <li className="text-[11px]" data-testid="work-product-progress-row">
      <div className="grid grid-cols-[minmax(0,1fr)_80px_64px] items-center gap-2">
        <span className="truncate flex items-center gap-1" title={wp.name}>
          {approved ? <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-600" data-testid={`work-product-approved-${wp.id}`} /> : <Package className="h-3 w-3 shrink-0 text-zinc-400" />} {wp.name}
        </span>
        <div className="h-1.5 bg-zinc-200"><div className={`h-full ${value >= 100 ? "bg-emerald-600" : "bg-zinc-900"}`} style={{ width: `${value}%` }} /></div>
        {canEdit ? (
          <div className="flex items-center">
            <Input type="number" min={0} max={100} value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }} className="rounded-none h-6 px-1 text-[11px] w-14 text-right" data-testid={`work-product-percent-input-${wp.id}`} />
            <span className="ml-0.5">%</span>
          </div>
        ) : (
          <span className="text-right font-mono" data-testid={`work-product-percent-${wp.id}`}>{value}%</span>
        )}
      </div>
      <DescriptionField value={description} canEdit={canEdit && !!onDescribe} onSave={(t) => onDescribe(wp.id, t)} wpId={wp.id} />
      {approved && (
        <div className="flex flex-wrap items-center gap-1.5 mt-1 pl-4">
          <span className="text-emerald-700">getest en goed bevonden</span>
          {canEdit && onTransfer && (
            <button onClick={() => onTransfer(wp)} className="inline-flex items-center gap-1 border border-zinc-900 px-1.5 py-0.5 hover:bg-zinc-900 hover:text-white transition-colors" data-testid={`transfer-work-product-button-${wp.id}`}>
              <Send className="h-3 w-3" /> Doorgeven
            </button>
          )}
          {sent.map((t) => (
            <span key={t.id} className={`px-1.5 py-0.5 border ${t.status === "rejected" ? "border-red-600 text-red-700" : t.status === "accepted" ? "border-emerald-600 text-emerald-700" : "border-zinc-300 text-zinc-500"}`} data-testid="sent-transfer-chip">
              → {t.to.label}{t.to.node_id !== t.from.node_id ? ` (${t.to.node_name})` : ""}: {STATUS[t.status]}
            </span>
          ))}
        </div>
      )}
      <ApproveDialog wp={wp} open={ask} onCancel={() => { setAsk(false); setV(value); }} onConfirm={() => { setAsk(false); setV(100); onSave(wp.id, 100, true).then((ok) => ok && onTransfer && onTransfer(wp)); }} />
    </li>
  );
}

export const WorkProductProgress = ({ goal, linked, canEdit, onProgress, transferCtx }) => {
  const [transferWp, setTransferWp] = useState(null);
  const items = linked.flatMap((s) => (s.work_products || []).map((wp) => ({ s, wp })));
  if (!items.length) return null;
  const progress = goal.progress || {};
  const approved = goal.approved || {};
  const avg = Math.round(items.reduce((t, { wp }) => t + (progress[wp.id] || 0), 0) / items.length);
  const save = async (wpId, pct, ok) => {
    try {
      await onProgress(goal.id, wpId, pct, ok);
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Opslaan mislukt");
      return false;
    }
  };
  const send = async (target) => {
    try {
      const { data } = await api.post("/transfers", { source: transferCtx.source, goal_id: goal.id, work_product_id: transferWp.id, target_node_id: target.node_id, target_system: target.system });
      toast.success(`Doorgegeven aan ${data.map((t) => t.to.label + (t.to.node_id !== t.from.node_id ? ` (${t.to.node_name})` : "")).join(", ")}`);
      setTransferWp(null);
      transferCtx.reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Doorgeven mislukt", { duration: 7000 });
    }
  };
  const describe = async (wpId, text) => {
    try {
      await api.put("/goals/work-product-description", { source: transferCtx.source, goal_id: goal.id, work_product_id: wpId, text });
      toast.success("Beschrijving opgeslagen");
      transferCtx.refreshGoals();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Opslaan mislukt");
    }
  };
  const sentFor = (wpId) => (transferCtx?.transfers || []).filter((t) => t.from.goal_id === goal.id && t.work_product_id === wpId);
  return (
    <div className="mt-2 w-full border border-zinc-200 bg-white p-2" data-testid="work-product-progress">
      <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
        <span>Werkproducten</span>
        <span data-testid="goal-progress-total">{avg}% gereed</span>
      </div>
      <ul className="space-y-1.5">
        {items.map(({ wp }) => (
          <ProgressRow key={wp.id} wp={wp} value={progress[wp.id] || 0} approved={!!approved[wp.id]} canEdit={canEdit} onSave={save} onTransfer={transferCtx ? setTransferWp : null} sent={sentFor(wp.id)} description={(goal.descriptions || {})[wp.id] || ""} onDescribe={transferCtx ? describe : null} />
        ))}
      </ul>
      {transferCtx && <TransferDialog wp={transferWp} open={!!transferWp} onClose={() => setTransferWp(null)} targets={transferCtx.targets} onSend={send} />}
    </div>
  );
};
